import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, Bus, ChevronUp, ChevronDown } from 'lucide-react';
import { Btn, Modal, FormField, Badge, inputCls } from '../ui';
import { useToast } from '../../contexts/NotificationContext';
import {
  getTransportProviders, createTransportProvider,
  updateTransportProvider, deleteTransportProvider,
} from '../../api';

function slugify(str) {
  return String(str)
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toLowerCase().trim()
    .replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
}

const EMPTY = { name: '', code: '', position: 0, isActive: true };

export default function HRTransportProviderManagement() {
  const toast = useToast();
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [codeManual, setCodeManual] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getTransportProviders();
      setProviders(data);
    } catch {
      toast.error('Erreur chargement fournisseurs');
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY, position: providers.length + 1 });
    setCodeManual(false);
    setShowModal(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({ name: p.name, code: p.code, position: p.position, isActive: p.isActive });
    setCodeManual(true);
    setShowModal(true);
  };

  const handleNameChange = (val) => {
    setForm(f => ({ ...f, name: val, ...(!codeManual ? { code: slugify(val) } : {}) }));
  };

  const handleCodeChange = (val) => {
    setCodeManual(true);
    setForm(f => ({ ...f, code: val }));
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.code.trim()) {
      toast.error('Nom et code sont obligatoires');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await updateTransportProvider(editing.id, form);
        toast.success('Fournisseur mis à jour');
      } else {
        await createTransportProvider(form);
        toast.success('Fournisseur ajouté');
      }
      setShowModal(false);
      fetch();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur enregistrement');
    }
    setSaving(false);
  };

  const handleDelete = async (p) => {
    try {
      await deleteTransportProvider(p.id);
      toast.success('Fournisseur supprimé');
      fetch();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur suppression');
    }
    setDeleteConfirm(null);
  };

  const movePosition = async (p, dir) => {
    await updateTransportProvider(p.id, { position: p.position + dir });
    fetch();
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Bus size={18} className="text-primary" />
          <h2 className="text-lg font-semibold text-gray-800">Fournisseurs de transport</h2>
        </div>
        <Btn variant="primary" size="sm" onClick={openCreate}>
          <Plus size={14} /> Ajouter
        </Btn>
      </div>

      <p className="text-sm text-gray-500">
        Ces fournisseurs apparaissent dans le sélecteur lors du dépôt d'un justificatif de transport.
        Le <strong>code</strong> est utilisé pour nommer automatiquement les fichiers (ex&nbsp;: <code>tcl_marie_04_2026.pdf</code>).
      </p>

      {loading ? (
        <div className="py-12 text-center text-sm text-gray-400">Chargement…</div>
      ) : providers.length === 0 ? (
        <div className="py-12 text-center text-sm text-gray-400">Aucun fournisseur configuré.</div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 w-8">Pos.</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">Nom</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">Code</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">Statut</th>
                <th className="px-4 py-2.5 text-right text-xs font-medium text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {providers.map((p, i) => (
                <tr key={p.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-gray-400 text-xs">
                    <div className="flex flex-col gap-0.5">
                      <button onClick={() => movePosition(p, -1)} disabled={i === 0} className="disabled:opacity-30 hover:text-primary"><ChevronUp size={12} /></button>
                      <button onClick={() => movePosition(p, 1)} disabled={i === providers.length - 1} className="disabled:opacity-30 hover:text-primary"><ChevronDown size={12} /></button>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-medium text-gray-800">{p.name}</td>
                  <td className="px-4 py-3 text-gray-500 font-mono text-xs">{p.code}</td>
                  <td className="px-4 py-3">
                    {p.isActive
                      ? <Badge color="bg-green-100 text-green-700">Actif</Badge>
                      : <Badge color="bg-gray-100 text-gray-500">Inactif</Badge>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      <Btn variant="ghost" size="sm" onClick={() => openEdit(p)}><Pencil size={13} /></Btn>
                      <Btn variant="ghost" size="sm" onClick={() => setDeleteConfirm(p)} className="text-red-500 hover:text-red-600"><Trash2 size={13} /></Btn>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal création / édition */}
      {showModal && (
        <Modal
          title={editing ? 'Modifier le fournisseur' : 'Nouveau fournisseur'}
          size="sm"
          onClose={() => setShowModal(false)}
          footer={
            <>
              <Btn variant="secondary" size="sm" onClick={() => setShowModal(false)}>Annuler</Btn>
              <Btn variant="primary" size="sm" loading={saving} onClick={handleSave}>Enregistrer</Btn>
            </>
          }
        >
          <div className="space-y-4">
            <FormField label="Nom" required>
              <input className={inputCls} value={form.name} onChange={e => handleNameChange(e.target.value)} placeholder="Ex : TCL Lyon" />
            </FormField>
            <FormField label="Code (tag fichier)" required>
              <input
                className={inputCls + ' font-mono'}
                value={form.code}
                onChange={e => handleCodeChange(e.target.value)}
                placeholder="Ex : tcl"
              />
              <p className="text-xs text-gray-400 mt-1">Minuscules, sans accents ni espaces. Auto-généré depuis le nom.</p>
            </FormField>
            <FormField label="Statut">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.isActive} onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))} />
                <span className="text-sm text-gray-700">Actif (visible dans le sélecteur)</span>
              </label>
            </FormField>
          </div>
        </Modal>
      )}

      {/* Modal confirmation suppression */}
      {deleteConfirm && (
        <Modal
          title="Supprimer le fournisseur"
          size="sm"
          onClose={() => setDeleteConfirm(null)}
          footer={
            <>
              <Btn variant="secondary" size="sm" onClick={() => setDeleteConfirm(null)}>Annuler</Btn>
              <Btn variant="danger" size="sm" onClick={() => handleDelete(deleteConfirm)}>Supprimer</Btn>
            </>
          }
        >
          <p className="text-sm text-gray-700">
            Supprimer <strong>{deleteConfirm.name}</strong> ? Si des documents l'utilisent déjà, la suppression sera bloquée — désactivez-le plutôt.
          </p>
        </Modal>
      )}
    </div>
  );
}
